import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import VendorVerification from "@/pages/VendorVerification";
import MenuServiceImport from "@/pages/MenuServiceImport";
import AvailabilityCalendar from "@/pages/AvailabilityCalendar";
import VendorEarnings from "@/pages/VendorEarnings";
import VendorPolicies from "@/pages/VendorPolicies";
import RefundReviews from "@/pages/RefundReviews";
import IntegrationsPanel from "@/pages/IntegrationsPanel";
import FileUploader from "@/components/FileUploader";
import { CATEGORIES, CATEGORY_FIELDS, CATEGORY_LABEL, FOOD_CATEGORIES, OFFERING_CATEGORIES } from "@/lib/categories";

const CATS = CATEGORIES.map(c => c.key);

export default function VendorDashboard() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [refundReviewCount, setRefundReviewCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", category: "catering", tagline: "", description: "", city: "", price_from: 0, price_unit: "per event", cover_image: "", gallery: [], services: [], contact_email: user?.email || "", contact_phone: "" };
  const [f, setF] = useState(empty);

  const load = () => {
    api.get("/vendors/mine/list").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data));
    api.get("/refund-reviews").then(({ data }) => setRefundReviewCount(data.length)).catch(() => setRefundReviewCount(0));
  };
  useEffect(() => { load(); }, []);

  const [addCatFor, setAddCatFor] = useState(null);
  const [addCat, setAddCat] = useState("");
  const submitAddCategory = async () => {
    if (!addCat) return toast.error("Choose a category");
    try {
      await api.post(`/vendors/${addCatFor.vendor_id}/categories`, { category: addCat });
      toast.success(`${CATEGORY_LABEL[addCat] || addCat} added`);
      setAddCatFor(null); setAddCat(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not add category"); }
  };
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (v) => { setEditing(v.vendor_id); setF({ ...v, gallery: v.gallery || [], services: v.services || [] }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...f, price_from: +f.price_from,
        gallery: typeof f.gallery === "string" ? f.gallery.split("\n").map(s => s.trim()).filter(Boolean) : f.gallery,
        services: typeof f.services === "string" ? f.services.split(",").map(s => s.trim()).filter(Boolean) : f.services };
      if (editing) await api.put(`/vendors/${editing}`, payload);
      else await api.post("/vendors", payload);
      toast.success(editing ? "Listing updated" : "Listing created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const galleryStr = Array.isArray(f.gallery) ? f.gallery.join("\n") : f.gallery;
  const servicesStr = Array.isArray(f.services) ? f.services.join(", ") : f.services;
  // Offerings tab (formerly Menus). All applicable vendor categories can use
  // it — food vendors see menu terminology, non-food vendors see "Packages &
  // Services". Label is picked from the FIRST offering-applicable vendor
  // the user owns; MenuServiceImport still per-vendor internally.
  const catsOf = (v) => [v.category, ...(v.additional_categories || [])];
  const offeringVendors = vendors.filter(v => catsOf(v).some(c => OFFERING_CATEGORIES.has(c)));
  const showMenusTab = offeringVendors.length > 0;
  const hasFoodVendor = offeringVendors.some(v => catsOf(v).some(c => FOOD_CATEGORIES.has(c)));
  const offeringsTabLabel = hasFoodVendor ? "Menus, Packages & Services" : "Packages & Services";

  return (
    <div data-testid="vendor-dashboard" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-10 gap-4">
        <div>
          <div className="eyebrow mb-3">Vendor Studio</div>
          <h1 className="font-serif text-4xl sm:text-5xl">Welcome, {user?.name?.split(" ")[0]}</h1>
        </div>
        {vendors.length === 0 && (
          <Button data-testid="new-listing-btn" onClick={openNew} className="btn-sage rounded-full h-11 px-6 self-start sm:self-auto">
            <Plus className="w-4 h-4 mr-2" /> New listing
          </Button>
        )}
      </div>

      <div className="mb-6 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 sm:p-5 flex items-center justify-between gap-3">
        <div className="text-sm">
          <div className="font-semibold">Complete your NContro profile</div>
          <div className="text-[color:var(--text-muted)] text-xs mt-1">Use the guided wizard to add branding, menus and verification documents.</div>
        </div>
        <Link data-testid="open-onboarding" to="/onboarding">
          <Button variant="outline" className="rounded-lg h-10 border-[color:var(--border-strong)]">Open wizard</Button>
        </Link>
      </div>

      <Tabs defaultValue="listings">
        {/* Mobile-safe tab row: constrains the inline-flex TabsList so only
            the tab bar scrolls horizontally on narrow screens, never the
            whole page. Desktop layout unchanged. */}
        <div className="w-full overflow-x-auto no-scrollbar">
          <TabsList className="bg-[#F5F2EB]">
            <TabsTrigger value="listings" data-testid="tab-listings">Listings</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">Quote requests</TabsTrigger>
            <TabsTrigger value="verification" data-testid="tab-verification">Verification</TabsTrigger>
            {showMenusTab && (
              <TabsTrigger value="menus" data-testid="tab-menus">{offeringsTabLabel}</TabsTrigger>
            )}
            <TabsTrigger value="policies" data-testid="tab-policies">Payments &amp; Policies</TabsTrigger>
            <TabsTrigger value="refunds" data-testid="tab-refunds" className="relative">
              Refunds
              {refundReviewCount > 0 && (
                <span
                  data-testid="refunds-badge"
                  className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#D9583A] text-white text-[10px] font-semibold"
                >
                  {refundReviewCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="listings" className="mt-6 sm:mt-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {vendors.length === 0 && <div className="text-[#5C5C5C]" data-testid="no-listings">No listings yet. Create one to be discovered.</div>}
            {vendors.map(v => (
              <div key={v.vendor_id} data-testid={`vendor-item-${v.vendor_id}`} className="card-elegant overflow-hidden">
                {v.cover_image && <img src={v.cover_image} alt="" className="w-full aspect-[16/10] object-cover" />}
                <div className="p-6">
                  <div className="flex flex-wrap gap-1.5 items-center" data-testid={`vendor-categories-${v.vendor_id}`}>
                    <span className="eyebrow">{CATEGORY_LABEL[v.category] || v.category}</span>
                    {(v.additional_categories || []).map(c => (
                      <span key={c} className="text-[10px] uppercase tracking-widest bg-[#EFEADF] px-2 py-0.5 rounded-full" data-testid={`vendor-category-chip-${v.vendor_id}-${c}`}>{CATEGORY_LABEL[c] || c}</span>
                    ))}
                    {(user?.user_id === v.owner_id || user?.role === "admin") && CATEGORIES.some(c => !catsOf(v).includes(c.key)) && (
                      <button type="button" data-testid={`add-category-btn-${v.vendor_id}`} onClick={() => { setAddCatFor(v); setAddCat(""); }}
                              className="text-[10px] uppercase tracking-widest text-[color:var(--accent)] hover:underline">+ Add a category</button>
                    )}
                  </div>
                  <div className="font-serif text-2xl mt-1">{v.name}</div>
                  <div className="text-sm text-[#5C5C5C]">{v.city}</div>
                  <div className="flex gap-2 mt-4">
                    <Link to={`/vendor/${v.vendor_id}`} className="text-sm link-underline">View</Link>
                    <button data-testid={`edit-vendor-${v.vendor_id}`} onClick={() => openEdit(v)} className="text-sm link-underline ml-auto">Edit</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="requests" className="mt-6 sm:mt-8">
          <div className="space-y-3">
            {bookings.length === 0 && <div className="text-[#5C5C5C]" data-testid="no-requests">No requests yet.</div>}
            {bookings.map(b => (
              <Link key={b.booking_id} to={`/bookings/${b.booking_id}`} data-testid={`request-${b.booking_id}`}
                className="card-elegant p-4 sm:p-5 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="eyebrow">{b.status}</div>
                  <div className="font-serif text-xl sm:text-2xl mt-1 truncate">{b.planner_name}</div>
                  <div className="text-xs sm:text-sm text-[#5C5C5C] truncate">{b.vendor_name} · {new Date(b.date).toLocaleDateString()} · {b.guest_count} guests</div>
                </div>
                <div className="font-serif text-xl sm:text-2xl shrink-0">${b.quote_amount}</div>
              </Link>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="verification" className="mt-6 sm:mt-8">
          <VendorVerification vendors={vendors} />
        </TabsContent>
        {showMenusTab && (
          <TabsContent value="menus" className="mt-6 sm:mt-8">
            <MenuServiceImport vendors={offeringVendors} />
          </TabsContent>
        )}
        <TabsContent value="policies" className="mt-6 sm:mt-8">
          <VendorPolicies vendors={vendors} />
        </TabsContent>
        <TabsContent value="refunds" className="mt-6 sm:mt-8">
          <RefundReviews scope="vendor" onCountChange={setRefundReviewCount} />
        </TabsContent>
        <TabsContent value="availability" className="mt-6 sm:mt-8">
          <AvailabilityCalendar vendors={vendors} />
        </TabsContent>
        <TabsContent value="integrations" className="mt-6 sm:mt-8">
          <IntegrationsPanel vendors={vendors} />
        </TabsContent>
        <TabsContent value="earnings" className="mt-6 sm:mt-8">
          <VendorEarnings vendors={vendors} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!addCatFor} onOpenChange={(o) => { if (!o) setAddCatFor(null); }}>
        <DialogContent data-testid="add-category-dialog" className="bg-[#FDFBF7] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Add a category</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C5C5C]">Your business stays one listing — it will also appear under the category you add.</p>
          <Select value={addCat} onValueChange={setAddCat}>
            <SelectTrigger data-testid="add-category-select" className="bg-white h-11"><SelectValue placeholder="Choose a category" /></SelectTrigger>
            <SelectContent>
              {addCatFor && CATEGORIES.filter(c => !catsOf(addCatFor).includes(c.key)).map(c => (
                <SelectItem key={c.key} value={c.key} data-testid={`add-category-option-${c.key}`}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button data-testid="add-category-submit" onClick={submitAddCategory} className="btn-sage rounded-full h-11 px-6">Add category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="listing-dialog" className="w-[calc(100vw-1rem)] max-w-2xl bg-[#FDFBF7] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-serif text-2xl sm:text-3xl">{editing ? "Edit listing" : "New listing"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="listing-name" placeholder="Name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="bg-white h-11" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select value={f.category} onValueChange={(v) => setF({...f, category: v})}>
                <SelectTrigger data-testid="listing-category" className="bg-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="listing-city" placeholder="City" value={f.city} onChange={e => setF({...f, city: e.target.value})} className="bg-white h-11" />
            </div>
            <Input placeholder="Tagline" value={f.tagline} onChange={e => setF({...f, tagline: e.target.value})} className="bg-white h-11" />
            <Textarea placeholder="Description" value={f.description} onChange={e => setF({...f, description: e.target.value})} className="bg-white" rows={4} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="number" placeholder="Price from" value={f.price_from} onChange={e => setF({...f, price_from: e.target.value})} className="bg-white h-11" />
              <Input placeholder="Price unit (per guest / per event)" value={f.price_unit} onChange={e => setF({...f, price_unit: e.target.value})} className="bg-white h-11" />
            </div>
            <Input placeholder="Cover image URL" value={f.cover_image} onChange={e => setF({...f, cover_image: e.target.value})} className="bg-white h-11" />
            <FileUploader
              kind="vendor_cover"
              value={f.cover_image}
              onUploaded={(url) => setF({...f, cover_image: url})}
              label="Upload cover image"
              testId="listing-cover-upload"
            />
            <Textarea placeholder="Gallery image URLs (one per line)" value={galleryStr} onChange={e => setF({...f, gallery: e.target.value})} className="bg-white" rows={3} />
            <FileUploader
              kind="vendor_gallery"
              onUploaded={(url) => {
                const list = Array.isArray(f.gallery) ? f.gallery : String(f.gallery || "").split("\n").filter(Boolean);
                setF({...f, gallery: [...list, url]});
              }}
              label="Add gallery image"
              testId="listing-gallery-upload"
            />
            <Input placeholder="Services (comma separated)" value={servicesStr} onChange={e => setF({...f, services: e.target.value})} className="bg-white h-11" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Contact email" value={f.contact_email} onChange={e => setF({...f, contact_email: e.target.value})} className="bg-white h-11" />
              <Input placeholder="Contact phone" value={f.contact_phone} onChange={e => setF({...f, contact_phone: e.target.value})} className="bg-white h-11" />
            </div>

            {/* Category-specific fields — only shown when relevant */}
            {(CATEGORY_FIELDS[f.category] || []).length > 0 && (
              <div className="pt-4 mt-4 border-t border-[color:var(--border)] space-y-3" data-testid="category-fields">
                <div className="eyebrow">
                  {f.category === "photography" ? "Photography details" : f.category === "transportation" ? "Fleet & rental details" : "Category details"}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CATEGORY_FIELDS[f.category].map(field => (
                    field.type === "select" ? (
                      <Select
                        key={field.key}
                        value={f[field.key] || ""}
                        onValueChange={(v) => setF({...f, [field.key]: v})}
                      >
                        <SelectTrigger
                          data-testid={`listing-${field.key}`}
                          className="bg-white h-11 col-span-2 sm:col-span-1"
                        >
                          <SelectValue placeholder={field.label} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        key={field.key}
                        data-testid={`listing-${field.key}`}
                        type={field.type === "number" ? "number" : field.type === "url" ? "url" : "text"}
                        placeholder={field.placeholder || field.label}
                        value={f[field.key] ?? ""}
                        onChange={e => setF({...f, [field.key]: field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value})}
                        min={field.min}
                        max={field.max}
                        className="bg-white h-11 col-span-2 sm:col-span-1"
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="save-listing-btn" className="btn-sage rounded-full w-full sm:w-auto" onClick={save}>Save listing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
